import * as Sentry from '@sentry/react';
import {
  ASYNC_OPERATION_QUERY_GROUP,
  getResultOperationResource,
  isAsyncOperationsListQueryKey,
  type OperationKey,
  type QueryGroup,
  type TanstackQueryClient,
  useRegionalCrossTeamApiResolver,
  useTeamSwitch,
} from '@vrplatform/data';
import { Button, Stack, Text } from '@mantine/core';
import {
  showErrorNotification,
  showSuccessNotification,
} from '@vrplatform/theme';
import {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as z from 'zod';
import { useStore } from 'zustand';
import { OperationPoller } from './OperationPoller';
import { useOperationSpans } from './useOperationSpans';
import { getTrackedOperationGroup } from './operationTracker.hooks';
import {
  createOperationTrackerStore,
  OperationTrackerContext,
  OperationTrackerLifetimeContext,
  type OperationTrackerStore,
} from './operationTracker.store';
import type {
  OperationEffect,
  OperationEffectTarget,
  OperationStoppedStatus,
  OperationSubmission,
  TrackedOperation,
} from './operationTracker.types';

const SUBMISSIONS_STORAGE_PREFIX = 'vrp:operation-submissions:';

const operationSubmissionsSchema = z.array(
  z.object({
    idempotencyKey: z.string().min(1),
    kind: z.string().min(1),
    teamId: z.string().min(1),
    request: z.string().min(1),
    resourceIds: z.array(z.string()),
    operationId: z.string().nullable(),
    createdAt: z.number(),
  })
) satisfies z.ZodType<OperationSubmission[]>;

const getSubmissionsStorageKey = (sessionId: string) =>
  `${SUBMISSIONS_STORAGE_PREFIX}${sessionId}`;

/**
 * Reads this session's unresolved submissions and drops entries left by other
 * sessions in this tab. Unreadable entries are removed and reported so they
 * cannot block later submissions.
 */
const readSessionSubmissions = (storageKey: string) => {
  for (let index = window.sessionStorage.length - 1; index >= 0; index--) {
    const key = window.sessionStorage.key(index);
    if (key?.startsWith(SUBMISSIONS_STORAGE_PREFIX) && key !== storageKey) {
      window.sessionStorage.removeItem(key);
    }
  }

  const stored = window.sessionStorage.getItem(storageKey);
  if (!stored) return [];

  try {
    return operationSubmissionsSchema.parse(JSON.parse(stored));
  } catch (error) {
    window.sessionStorage.removeItem(storageKey);
    Sentry.captureException(error, {
      tags: { async_operation_effect_kind: 'submission-hydration' },
    });
    return [];
  }
};

const OPERATION_LIST_EFFECT_ID = '\0operation-list-invalidation';
const OPERATION_EFFECT_RETRY_DELAY_MS = 5_000;
const OPERATION_EFFECT_MAX_ATTEMPTS = 2;

const isStoppedOperation = (
  operation: TrackedOperation
): operation is TrackedOperation & { status: OperationStoppedStatus } =>
  operation.status === 'completed' ||
  operation.status === 'failed' ||
  operation.status === 'unavailable' ||
  operation.status === 'error';

const matchesEffectTarget = (
  meta: Readonly<Record<string, unknown>> | undefined,
  target: OperationEffectTarget,
  includeUnscoped: boolean
) => {
  if (target.scope === 'global') return true;
  if (meta?.teamScoped === false) return true;
  if (meta?.teamScoped === true) {
    return (
      typeof meta.teamId === 'string' && target.teamIds.includes(meta.teamId)
    );
  }

  return includeUnscoped;
};

const shouldRunEffect = (
  effect: OperationEffect,
  status: OperationStoppedStatus
) => {
  if (effect.runOn && !effect.runOn.includes(status)) return false;
  if (effect.kind === 'notification') return effect.status === status;

  return true;
};

const reportEffectError = (error: unknown, effect: OperationEffect) => {
  Sentry.captureException(error, {
    tags: {
      async_operation_effect_id: effect.id,
      async_operation_effect_kind: effect.kind,
    },
  });
};

const reportOperationListInvalidationError = (error: unknown) => {
  Sentry.captureException(error, {
    tags: {
      async_operation_effect_kind: 'operations-list-invalidation',
    },
  });
};

const getVerifiedResultTeamId = async (
  operation: TrackedOperation,
  getRegionalCrossTeamApi: ReturnType<typeof useRegionalCrossTeamApiResolver>
) => {
  if (!('resources' in operation.operation)) {
    throw new Error('Completed team operation has no result resources');
  }

  const result = getResultOperationResource(operation.operation);
  if (!result || result.type !== 'team') {
    throw new Error('Completed team operation has no result team');
  }
  if (operation.reference.placement.apiTarget !== 'regional') {
    throw new Error('Result-team verification requires regional placement');
  }

  const response = await getRegionalCrossTeamApi(
    operation.reference.placement.regionalApiBaseUrl
  ).GET('/teams', {
    headers: {
      'x-data-region': operation.reference.placement.dataRegion,
      'x-team-id': operation.reference.requesterTeamId,
    },
    params: {
      query: {
        ids: result.id,
        limit: 1,
        page: 1,
      },
    },
  });
  if (response.error) throw response.error;
  if (!response.data.data.some((team) => team.id === result.id)) {
    throw new Error('Result team is not readable by the operation requester');
  }

  return result.id;
};

const runEffect = async (
  queryClient: TanstackQueryClient,
  effect: OperationEffect,
  status: OperationStoppedStatus,
  failureMessage: string | undefined,
  operation: TrackedOperation | undefined,
  getRegionalCrossTeamApi: ReturnType<typeof useRegionalCrossTeamApiResolver>,
  openTeam: ReturnType<typeof useTeamSwitch>
) => {
  if (effect.kind === 'notification') {
    const showNotification =
      status === 'completed' ? showSuccessNotification : showErrorNotification;
    let resultTeamId: string | null = null;
    if (effect.action) {
      if (!operation) {
        throw new Error('Result-team action requires a tracked operation');
      }
      resultTeamId = await getVerifiedResultTeamId(
        operation,
        getRegionalCrossTeamApi
      );
    }
    showNotification({
      id: effect.id,
      title: effect.title,
      message:
        effect.action && resultTeamId ? (
          <Stack gap="xs" align="flex-start">
            <Text size="sm">{effect.message}</Text>
            <Button
              size="xs"
              variant="default"
              onClick={() => void openTeam(resultTeamId)}
            >
              {effect.action.label}
            </Button>
          </Stack>
        ) : effect.includeFailureMessage && failureMessage ? (
          `${effect.message} ${failureMessage}`
        ) : (
          effect.message
        ),
      ...(effect.icon ? { icon: effect.icon, color: 'transparent' } : {}),
      ...(effect.action ? { autoClose: false } : {}),
    });
    return;
  }

  if (effect.kind === 'api') {
    await Promise.all(
      effect.groups.map((group: QueryGroup) =>
        queryClient.invalidateQueries({
          queryKey: [group],
          type: 'all',
          predicate: ({ meta, queryKey }) =>
            matchesEffectTarget(meta, effect.target, false) &&
            (!effect.includeUniqueQueryKeys?.length ||
              (typeof queryKey[1] === 'string' &&
                effect.includeUniqueQueryKeys.includes(queryKey[1]))),
        })
      )
    );
    return;
  }

  await Promise.all(
    effect.queryKeys.map((queryKey) =>
      queryClient.invalidateQueries({
        queryKey: [queryKey],
        type: 'all',
        // Legacy Hasura keys have no tenant metadata. Marking that exact key
        // stale is the only safe reconciliation when the affected team is not
        // the selected team; API queries still use their captured metadata.
        predicate: ({ meta }) => matchesEffectTarget(meta, effect.target, true),
      })
    )
  );
};

function OperationEffectExecutor({
  queryClient,
  store,
}: {
  queryClient: TanstackQueryClient;
  store: OperationTrackerStore;
}) {
  const getRegionalCrossTeamApi = useRegionalCrossTeamApiResolver();
  const openTeam = useTeamSwitch();
  const operations = useStore(store, (state) => state.operations);
  const groups = useStore(store, (state) => state.groups);
  const listInvalidationsRef = useRef(new Map<OperationKey, Promise<void>>());
  const pendingEffectsRef = useRef(new Set<string>());
  const effectAttemptsRef = useRef(new Map<string, number>());
  const isExecutorMountedRef = useRef(true);
  const [effectRetryVersion, setEffectRetryVersion] = useState(0);

  useOperationSpans(store);

  useEffect(() => {
    isExecutorMountedRef.current = true;
    return () => {
      isExecutorMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const scheduleEffectRetry = (executionKey: string) => {
      if (!isExecutorMountedRef.current) {
        pendingEffectsRef.current.delete(executionKey);
        return;
      }

      window.setTimeout(() => {
        pendingEffectsRef.current.delete(executionKey);
        if (isExecutorMountedRef.current) {
          setEffectRetryVersion((version) => version + 1);
        }
      }, OPERATION_EFFECT_RETRY_DELAY_MS);
    };

    const runTrackedEffect = (
      executionKey: string,
      effect: OperationEffect,
      execute: () => Promise<void>,
      claim: () => void
    ) => {
      const attempts = effectAttemptsRef.current.get(executionKey) ?? 0;
      if (
        pendingEffectsRef.current.has(executionKey) ||
        attempts >= OPERATION_EFFECT_MAX_ATTEMPTS
      ) {
        return;
      }

      pendingEffectsRef.current.add(executionKey);
      effectAttemptsRef.current.set(executionKey, attempts + 1);
      void execute()
        .then(() => {
          pendingEffectsRef.current.delete(executionKey);
          effectAttemptsRef.current.delete(executionKey);
          claim();
        })
        .catch((error: unknown) => {
          reportEffectError(error, effect);
          if (
            (effectAttemptsRef.current.get(executionKey) ?? 0) <
            OPERATION_EFFECT_MAX_ATTEMPTS
          ) {
            scheduleEffectRetry(executionKey);
          } else {
            pendingEffectsRef.current.delete(executionKey);
          }
        });
    };

    for (const operation of Object.values(operations)) {
      if (!isStoppedOperation(operation)) continue;

      const listEffectExecutionKey = JSON.stringify([
        'operation',
        operation.key,
        OPERATION_LIST_EFFECT_ID,
      ]);
      let listInvalidation = listInvalidationsRef.current.get(operation.key);
      if (!listInvalidation) {
        if (
          operation.claimedEffectIds.includes(OPERATION_LIST_EFFECT_ID) ||
          pendingEffectsRef.current.has(listEffectExecutionKey) ||
          (effectAttemptsRef.current.get(listEffectExecutionKey) ?? 0) >=
            OPERATION_EFFECT_MAX_ATTEMPTS
        ) {
          listInvalidation = Promise.resolve();
        } else {
          pendingEffectsRef.current.add(listEffectExecutionKey);
          effectAttemptsRef.current.set(
            listEffectExecutionKey,
            (effectAttemptsRef.current.get(listEffectExecutionKey) ?? 0) + 1
          );
          listInvalidation = queryClient
            .invalidateQueries({
              queryKey: [ASYNC_OPERATION_QUERY_GROUP],
              type: 'all',
              predicate: ({ queryKey }) =>
                isAsyncOperationsListQueryKey(queryKey, operation.reference),
            })
            .then(() => {
              pendingEffectsRef.current.delete(listEffectExecutionKey);
              effectAttemptsRef.current.delete(listEffectExecutionKey);
              store
                .getState()
                .claimOperationEffect(operation.key, OPERATION_LIST_EFFECT_ID);
            })
            .catch((error: unknown) => {
              reportOperationListInvalidationError(error);
              listInvalidationsRef.current.delete(operation.key);
              if (
                (effectAttemptsRef.current.get(listEffectExecutionKey) ?? 0) <
                OPERATION_EFFECT_MAX_ATTEMPTS
              ) {
                scheduleEffectRetry(listEffectExecutionKey);
              } else {
                pendingEffectsRef.current.delete(listEffectExecutionKey);
              }
            });
          listInvalidationsRef.current.set(operation.key, listInvalidation);
        }
      }

      for (const effect of operation.effects) {
        if (
          !shouldRunEffect(effect, operation.status) ||
          operation.claimedEffectIds.includes(effect.id)
        ) {
          continue;
        }

        runTrackedEffect(
          JSON.stringify(['operation', operation.key, effect.id]),
          effect,
          () =>
            listInvalidation.then(() =>
              runEffect(
                queryClient,
                effect,
                operation.status,
                operation.failure?.message,
                operation,
                getRegionalCrossTeamApi,
                openTeam
              )
            ),
          () => {
            store.getState().claimOperationEffect(operation.key, effect.id);
          }
        );
      }
    }

    for (const group of Object.values(groups)) {
      const trackedGroup = getTrackedOperationGroup(group, operations);
      if (!trackedGroup || trackedGroup.state === 'active') continue;

      const status: OperationStoppedStatus =
        trackedGroup.state === 'completed'
          ? 'completed'
          : trackedGroup.state === 'partialFailure'
            ? 'failed'
            : 'unavailable';
      const memberInvalidations = trackedGroup.operationKeys.map(
        (key) => listInvalidationsRef.current.get(key) ?? Promise.resolve()
      );
      const failureMessage = trackedGroup.failedOperationKeys
        .map((key) => operations[key]?.failure?.message)
        .find((message) => message !== undefined);

      for (const effect of trackedGroup.effects) {
        if (
          !shouldRunEffect(effect, status) ||
          group.claimedEffectIds.includes(effect.id)
        ) {
          continue;
        }

        runTrackedEffect(
          JSON.stringify(['group', group.groupId, effect.id]),
          effect,
          () =>
            Promise.all(memberInvalidations).then(() =>
              runEffect(
                queryClient,
                effect,
                status,
                failureMessage,
                undefined,
                getRegionalCrossTeamApi,
                openTeam
              )
            ),
          () => {
            store.getState().claimGroupEffect(group.groupId, effect.id);
          }
        );
      }
    }
  }, [
    getRegionalCrossTeamApi,
    groups,
    openTeam,
    operations,
    queryClient,
    effectRetryVersion,
    store,
  ]);

  return null;
}

export function OperationTrackerProvider({
  children,
  queryClient,
  sessionId,
}: PropsWithChildren<{
  queryClient: TanstackQueryClient;
  sessionId: string;
}>) {
  if (!sessionId) throw new Error('Operation tracker requires a session ID');

  const [store] = useState(createOperationTrackerStore);
  const lifetimeController = useMemo(
    () => new AbortController(),
    [queryClient, sessionId]
  );
  const operations = useStore(store, (state) => state.operations);

  useEffect(() => {
    const key = getSubmissionsStorageKey(sessionId);
    if (store.getState().submissionsPersistence?.key === key) return;

    store
      .getState()
      .hydrateSubmissions(
        { storage: window.sessionStorage, key },
        readSessionSubmissions(key)
      );
  }, [sessionId, store]);

  useEffect(
    () => () => {
      lifetimeController.abort();
      store.getState().reset();
      const removeOperationQueries = () =>
        queryClient.removeQueries({
          queryKey: [ASYNC_OPERATION_QUERY_GROUP],
        });

      void queryClient
        .cancelQueries({ queryKey: [ASYNC_OPERATION_QUERY_GROUP] })
        .finally(removeOperationQueries);
      removeOperationQueries();
    },
    [lifetimeController, queryClient, sessionId, store]
  );

  return (
    <OperationTrackerLifetimeContext.Provider value={lifetimeController.signal}>
      <OperationTrackerContext.Provider value={store}>
        {children}
        {Object.values(operations).map((operation) =>
          operation.status === 'queued' || operation.status === 'running' ? (
            <OperationPoller
              key={operation.key}
              operationKey={operation.key}
              reference={operation.reference}
            />
          ) : null
        )}
        <OperationEffectExecutor
          key={sessionId}
          queryClient={queryClient}
          store={store}
        />
      </OperationTrackerContext.Provider>
    </OperationTrackerLifetimeContext.Provider>
  );
}
